import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useUser } from '@client/hooks/use-user.hooks';
import { useInitSentry } from '@client/hooks/use-sentry.hook';
import { isAdmin } from '@shared/helpers/user.helpers';
import { SENTRY_TEST_MESSAGE_PARAM } from '@shared/helpers/sentry-test.helpers';
import { SentryTestThrowBoundary } from './SentryTestThrowBoundary';
import { ThrowSentryTestError } from './ThrowSentryTestError';
import {
  type SentryTestServerState,
  useSentryTest,
} from './use-sentry-test.hooks';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    overflow: 'auto',
    height: '100%',
  },
  form: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    alignItems: 'center',
  },
}));

const SERVER_STATE_LABEL: Record<SentryTestServerState, string> = {
  idle: 'Not called',
  thrown: 'Server threw (expected)',
  failed: 'Server request failed (not the expected 500)',
  'unexpected-ok': 'Server returned OK — the test endpoint did not throw',
};

export const AdminSentryTest: React.FC = () => {
  const { classes } = useStyles();
  useInitSentry();
  const user = useUser();
  const disabled = useMemo(() => !user || !isAdmin(user), [user]);
  const {
    rawMessage,
    setRawMessage,
    errorMessage,
    serverState,
    shouldThrowClient,
    startRun,
    runId,
  } = useSentryTest({ enabled: !disabled });
  const [draftMessage, setDraftMessage] = useState(rawMessage);

  const onSubmit = useCallback<React.FormEventHandler>(
    (event) => {
      event.preventDefault();
      setRawMessage(draftMessage);
      startRun(draftMessage);
    },
    [draftMessage, setRawMessage, startRun],
  );

  if (disabled) {
    return (
      <Box
        sx={{
          p: 4,
        }}
      >
        <Typography variant="h6">Not allowed</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Typography variant="h6">Sentry test</Typography>
      <Typography variant="body1">
        Set <code>?{SENTRY_TEST_MESSAGE_PARAM}=</code> to throw the same error
        on this page and on the server. Use a unique value each time so Sentry
        opens a new issue. Client Sentry is off on localhost — use test.nodon.se
        or production.
      </Typography>
      <Box component="form" className={classes.form} onSubmit={onSubmit}>
        <TextField
          size="small"
          label={SENTRY_TEST_MESSAGE_PARAM}
          value={draftMessage}
          onChange={(event) => setDraftMessage(event.target.value)}
        />
        <Button type="submit" variant="contained" size="small">
          Throw
        </Button>
      </Box>
      <Typography variant="body2">
        Server: {SERVER_STATE_LABEL[serverState]}
      </Typography>
      {shouldThrowClient && errorMessage ? (
        <SentryTestThrowBoundary
          key={`${errorMessage}-${runId}`}
          fallback={
            <Typography variant="body2">
              Client threw: {errorMessage}
            </Typography>
          }
        >
          <ThrowSentryTestError message={errorMessage} />
        </SentryTestThrowBoundary>
      ) : (
        <Typography variant="body2">Client: not thrown</Typography>
      )}
    </Box>
  );
};
